import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRightLeft,
  Ban,
  Banknote,
  CalendarPlus,
  Check,
  LogOut,
  Minus,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import AccessCountdown, { pluralDays } from '@/components/AccessCountdown';
import TenantCuisinesCard from '@/components/TenantCuisinesCard';
import {
  activatePaidPeriod as apiActivatePaid,
  adjustTenantTrial,
  attachTenantAddon,
  changeTenantPlan as apiChangeTenantPlan,
  deleteTenant as apiDeleteTenant,
  detachTenantAddon,
  detachTenantOwner,
  extendTenantTrial,
  getAddonCatalog,
  getTenant,
  listManagers,
  listPlans,
  assignTenantReferrer,
  reduceTenantAccess,
  revokeDevice,
  revokeTenantOwnerSessions,
  setTenantAddonQuantity,
  setTenantStatus,
  updateTenant as apiUpdateTenant,
  type ActivatePaidBody,
  type AddonCatalogItem,
  type ChangeTenantPlanBody,
  type ExistingOwnerInfo,
  type LimitInfo,
  type PlanItem,
  type TenantDetail as TenantDetailDto,
  type UpdateTenantBody,
} from '@/lib/operatorApi';
import { ApiError } from '@/lib/api';
import {
  formatDate,
  formatDateTime,
  formatKop,
  formatNumber,
  kopToRub,
  relativeTime,
} from '@/lib/format';

export default function TenantDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<TenantDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [modal, setModal] = useState<
    | null
    | { kind: 'trial' }
    | { kind: 'plan' }
    | { kind: 'cash' }
    | { kind: 'activate' }
    | { kind: 'addon' }
    | { kind: 'reduce' }
    | { kind: 'delete' }
    | { kind: 'detachOwner' }
  >(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getTenant(id)
      .then((t) => setTenant(t))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const flash = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2500);
  };

  const handleBlock = async () => {
    if (!id) return;
    if (!window.confirm('Заблокировать подписку арендатора? Они потеряют доступ.')) return;
    const reason = window.prompt('Причина (для аудита):') ?? undefined;
    try {
      setActionBusy(true);
      await setTenantStatus(id, 'CANCELLED', reason || undefined);
      await load();
      flash('Подписка заблокирована');
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setActionBusy(false);
    }
  };

  const handleUnblock = async () => {
    if (!id) return;
    if (!window.confirm('Разблокировать подписку и вернуть в активный статус?')) return;
    try {
      setActionBusy(true);
      await setTenantStatus(id, 'ACTIVE');
      await load();
      flash('Подписка активирована');
    } catch (e) {
      // Срок истёк — статуса недостаточно, бэкенд просит задать период.
      // Сразу открываем форму, чтобы оператор не гадал, что делать дальше.
      window.alert(e instanceof Error ? e.message : 'Ошибка');
      setModal({ kind: 'activate' });
    } finally {
      setActionBusy(false);
    }
  };

  const handleRevokeOwnerSessions = async () => {
    if (!id || !tenant) return;
    const who = tenant.ownerName || tenant.ownerEmail;
    const ok = window.confirm(
      `Завершить все сессии владельца${who ? ` (${who})` : ''}?\n\nВыйдет из админки, личного кабинета и приложения на всех устройствах, push-подписки снимутся. Войдёт заново по коду на почту${tenant.ownerEmail ? ` ${tenant.ownerEmail}` : ''}.`,
    );
    if (!ok) return;
    try {
      setActionBusy(true);
      await revokeTenantOwnerSessions(id);
      await load();
      flash('Сессии владельца завершены');
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setActionBusy(false);
    }
  };

  const handleRevokeDevice = async (deviceId: string) => {
    if (!window.confirm('Отозвать устройство? Оно перестанет работать немедленно.')) return;
    try {
      setActionBusy(true);
      await revokeDevice(deviceId);
      await load();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Ошибка отзыва');
    } finally {
      setActionBusy(false);
    }
  };

  if (loading && !tenant) {
    return <div className="p-10 text-ink/50">Загрузка…</div>;
  }

  if (error && !tenant) {
    return (
      <div className="p-10">
        <Link to="/tenants" className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-ink">
          <ArrowLeft size={14} /> К списку
        </Link>
        <p className="mt-6 text-red-600">{error}</p>
      </div>
    );
  }

  if (!tenant || !id) return null;

  const avgOrder = tenant.usage.ordersLast30d > 0
    ? Math.round(tenant.usage.revenueLast30d / tenant.usage.ordersLast30d)
    : null;

  const isCancelled = tenant.subscription?.status === 'cancelled';
  // Удаление точки сносит пользователей с домашней здесь, кроме владельца сети
  // с другими точками — он остаётся (см. deleteTenant).
  const ownerHomeHere = !!tenant.ownerId && tenant.users.some((u) => u.id === tenant.ownerId);
  const ownerStaysOnDelete = !ownerHomeHere || tenant.network.ownerEstablishmentCount > 1;

  return (
    <div>
      <PageHeader
        eyebrow={`Арендатор · ${tenant.id}`}
        title={tenant.name}
        description={[tenant.ownerName, tenant.city].filter(Boolean).join(' · ') || '—'}
        actions={
          <>
            <Link to="/tenants" className="btn-ghost">
              <ArrowLeft size={14} strokeWidth={1.75} />
              Назад
            </Link>
            <button
              className="btn-outline"
              type="button"
              onClick={() => setModal({ kind: 'trial' })}
              disabled={actionBusy}
            >
              <CalendarPlus size={14} strokeWidth={1.75} />
              Продлить trial
            </button>
            <button
              className="btn-outline"
              type="button"
              onClick={() => setModal({ kind: 'plan' })}
              disabled={actionBusy}
            >
              <Sparkles size={14} strokeWidth={1.75} />
              Сменить тариф
            </button>
            <button
              className="btn-outline"
              type="button"
              onClick={() => setModal({ kind: 'cash' })}
              disabled={actionBusy}
            >
              <Banknote size={14} strokeWidth={1.75} />
              Оплата наличными
            </button>
            <button
              className="btn-outline"
              type="button"
              onClick={() => setModal({ kind: 'activate' })}
              disabled={actionBusy}
            >
              <Check size={14} strokeWidth={1.75} />
              Перевести в Active
            </button>
            {isCancelled ? (
              <button
                className="btn-primary"
                type="button"
                onClick={handleUnblock}
                disabled={actionBusy}
              >
                <Check size={14} strokeWidth={1.75} />
                Разблокировать
              </button>
            ) : (
              <button
                className="btn-danger"
                type="button"
                onClick={handleBlock}
                disabled={actionBusy}
              >
                <Ban size={14} strokeWidth={1.75} />
                Заблокировать
              </button>
            )}
          </>
        }
      />

      {toast && (
        <div className="mx-4 mt-4 border border-green-600/30 bg-green-50 px-4 py-2 text-[13px] text-green-700 md:mx-8">
          {toast}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 px-4 py-6 md:gap-6 md:px-8 md:py-8 lg:grid-cols-3">
        <SubscriptionCard
          tenant={tenant}
          onQuickExtend={async (days) => {
            try {
              setActionBusy(true);
              await extendTenantTrial(tenant.id, days);
              await load();
              flash(`Триал продлён на ${days} дн.`);
            } catch (e) {
              window.alert(e instanceof Error ? e.message : 'Ошибка продления');
            } finally {
              setActionBusy(false);
            }
          }}
          quickBusy={actionBusy}
          onReduce={() => setModal({ kind: 'reduce' })}
        />
        <ProfileCard
          tenant={tenant}
          onSaved={async (msg) => { await load(); flash(msg ?? 'Данные сохранены'); }}
          onTransferOwner={() => setModal({ kind: 'detachOwner' })}
          onRevokeOwnerSessions={handleRevokeOwnerSessions}
          ownerActionsBusy={actionBusy}
        />
        <UsageCard tenant={tenant} avgOrder={avgOrder} />
      </div>

      <ReferrerSection
        tenantId={id}
        tenant={tenant}
        onChanged={async () => {
          await load();
          flash('Менеджер обновлён');
        }}
      />

      <AddonsSection
        tenantId={id}
        tenant={tenant}
        onChanged={async (msg) => { await load(); flash(msg); }}
        onAttach={() => setModal({ kind: 'addon' })}
      />

      <DevicesSection
        tenant={tenant}
        onRevoke={handleRevokeDevice}
        onRefresh={load}
        actionBusy={actionBusy}
      />

      <TenantCuisinesCard tenantId={id} onSaved={flash} />

      <PaymentsSection tenant={tenant} />

      <DangerZone onDelete={() => setModal({ kind: 'delete' })} disabled={actionBusy} />

      {modal?.kind === 'detachOwner' && (
        <DetachOwnerModal
          tenant={tenant}
          onClose={() => setModal(null)}
          onDone={async (msg) => {
            setModal(null);
            await load();
            flash(msg);
          }}
        />
      )}

      {modal?.kind === 'delete' && (
        <DeleteTenantModal
          tenantId={id}
          tenantName={tenant.name}
          ownerEmail={tenant.ownerEmail}
          ownerStays={ownerStaysOnDelete}
          usersCount={tenant.users.length - (ownerHomeHere && ownerStaysOnDelete ? 1 : 0)}
          onClose={() => setModal(null)}
          onDeleted={(name) => {
            setModal(null);
            navigate('/tenants', {
              replace: true,
              state: { flash: `Аккаунт «${name}» полностью удалён` },
            });
          }}
        />
      )}

      {modal?.kind === 'trial' && (
        <TrialModal
          tenantId={id}
          currentTrialEnd={tenant.subscription?.trialEndsAt ?? null}
          onClose={() => setModal(null)}
          onSaved={async () => {
            setModal(null);
            await load();
            flash('Trial продлён');
          }}
        />
      )}

      {modal?.kind === 'plan' && (
        <PlanModal
          tenantId={id}
          currentPlanCode={tenant.subscription?.planDetails?.code ?? tenant.subscription?.plan ?? null}
          currentBillingPeriod={tenant.subscription?.billingPeriod ?? 'MONTHLY'}
          onClose={() => setModal(null)}
          onSaved={async () => {
            setModal(null);
            await load();
            flash('Тариф обновлён');
          }}
        />
      )}

      {modal?.kind === 'cash' && (
        <CashPaymentModal
          tenantId={id}
          sub={tenant.subscription}
          onClose={() => setModal(null)}
          onSaved={async () => {
            setModal(null);
            await load();
            flash('Оплата активирована · статус ACTIVE');
          }}
        />
      )}

      {modal?.kind === 'activate' && (
        <CashPaymentModal
          variant="manual"
          tenantId={id}
          sub={tenant.subscription}
          onClose={() => setModal(null)}
          onSaved={async () => {
            setModal(null);
            await load();
            flash('Статус ACTIVE · срок продлён без платежа');
          }}
        />
      )}

      {modal?.kind === 'addon' && (
        <AddonPickerModal
          tenantId={id}
          existingCodes={(tenant.subscription?.addons ?? [])
            .filter((a) => a.renewing !== false)
            .map((a) => a.code)}
          onClose={() => setModal(null)}
          onSaved={async () => {
            setModal(null);
            await load();
            flash('Модуль подключён');
          }}
        />
      )}

      {modal?.kind === 'reduce' && tenant.subscription && (
        <ReduceAccessModal
          tenantId={id}
          sub={tenant.subscription}
          onClose={() => setModal(null)}
          onSaved={async (endsAt) => {
            setModal(null);
            await load();
            flash(endsAt ? `Срок уменьшен: до ${formatDate(endsAt)}` : 'Срок уменьшен');
          }}
        />
      )}
    </div>
  );
}

// ─── Subscription card ───────────────────────────────────────────

function SubscriptionCard({
  tenant,
  onQuickExtend,
  quickBusy,
  onReduce,
}: {
  tenant: TenantDetailDto;
  onQuickExtend: (days: number) => Promise<void> | void;
  quickBusy: boolean;
  onReduce: () => void;
}) {
  const sub = tenant.subscription;
  const planName = sub?.planDetails?.name ?? sub?.plan ?? '—';
  const periodLabel = sub?.billingPeriod === 'YEARLY' ? 'год' : 'мес';
  const isTrial = sub?.status === 'trial';
  const trialEnd = sub?.trialEndsAt ?? null;
  // Модули входят в сумму подписки (× 12 за год) — показываем раскладку.
  // Отключённые, но дорабатывающие срок в сумму уже не входят.
  const addonsPeriod = sub
    ? sub.addons
        .filter((a) => a.renewing !== false)
        .reduce((sum, a) => sum + a.priceMonthKopecks * a.quantity, 0) *
      (sub.billingPeriod === 'YEARLY' ? 12 : 1)
    : 0;

  return (
    <section className="card p-6">
      <h2 className="eyebrow">Подписка</h2>
      <div className="mt-3 flex items-center gap-3">
        <StatusBadge status={sub?.status ?? 'trial'} />
        <span className="text-[22px] font-medium tracking-tightest">
          {sub ? formatKop(sub.amount) : '—'}
          <span className="text-[13px] font-normal text-ink/50"> / {periodLabel}</span>
        </span>
      </div>
      {sub && addonsPeriod > 0 && (
        <div className="mt-1 text-[12px] text-ink/50">
          тариф {formatKop(Math.max(0, sub.amount - addonsPeriod))} + модули {formatKop(addonsPeriod)}
        </div>
      )}

      {!sub && (
        <div className="mt-3 border border-line bg-paper px-3 py-2 text-[12px] leading-relaxed text-ink/70">
          Своей подписки у этой точки ещё нет. Она заведётся первым же действием отсюда —
          «Продлить trial», «Оплата наличными» или «Сменить тариф».
        </div>
      )}

      {tenant.network.ownerEstablishmentCount > 1 && (
        <div className="mt-3 border border-line bg-paper px-3 py-2 text-[12px] leading-relaxed text-ink/70">
          У владельца {tenant.network.ownerEstablishmentCount}{' '}
          {pluralEstablishments(tenant.network.ownerEstablishmentCount)}, у каждого своя подписка.
          Продление, оплата и тариф здесь касаются только{' '}
          <span className="font-medium text-ink">«{tenant.name}»</span> — остальные точки
          оплачиваются отдельно из своих карточек.
        </div>
      )}

      {sub?.accessEndsAt && (
        <div className="mt-4">
          <AccessCountdown
            endsAt={sub.accessEndsAt}
            subject={isTrial ? 'trial' : 'paid'}
            startedAt={isTrial ? tenant.createdAt : sub.currentPeriodStart}
            accessState={sub.accessState}
            graceDaysLeft={sub.graceDaysLeft}
            variant="full"
          />
        </div>
      )}

      {isTrial && trialEnd && (
        <div className="mt-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] uppercase tracking-[0.08em] text-ink/50">
              быстро добавить:
            </span>
            {[1, 7, 14, 30].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => onQuickExtend(n)}
                disabled={quickBusy}
                className="border border-line px-2 py-1 text-[11px] font-medium text-ink/80 hover:border-ink hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
              >
                +{n}{n === 1 ? ' день' : n < 5 ? ' дня' : ' дней'}
              </button>
            ))}
          </div>
        </div>
      )}

      {sub?.accessEndsAt && (
        <div className="mt-2">
          <button
            type="button"
            onClick={onReduce}
            disabled={quickBusy}
            className="inline-flex items-center gap-1 border border-line px-2 py-1 text-[11px] font-medium text-ink/70 hover:border-ink hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Minus size={12} strokeWidth={1.75} />
            Уменьшить срок
          </button>
        </div>
      )}

      <dl className="mt-6 space-y-3 text-[13px]">
        <Row label="Создан" value={formatDate(tenant.createdAt)} />
        <Row
          label="Следующее списание"
          value={
            sub?.currentPeriodEnd
              ? formatDate(sub.currentPeriodEnd)
              : sub?.trialEndsAt
                ? `Trial до ${formatDate(sub.trialEndsAt)}`
                : '—'
          }
        />
        <Row
          label="Осталось дней"
          value={
            sub?.daysLeft == null
              ? '—'
              : sub.accessState === 'blocked'
                ? 'доступ закрыт'
                : sub.accessState === 'grace'
                  ? `просрочен · ${sub.graceDaysLeft} ${pluralDays(sub.graceDaysLeft)} до отключения`
                  : `${sub.daysLeft} ${pluralDays(sub.daysLeft)}`
          }
        />
        <Row label="Тариф" value={planName} />
        <Row label="Период" value={sub?.billingPeriod === 'YEARLY' ? 'Годовой' : 'Месячный'} />
        {sub?.additionalEstablishments ? (
          <Row label="Доп. заведения" value={`+${sub.additionalEstablishments}`} />
        ) : null}
        {tenant.limits && (
          <>
            <Row label="Кассы" value={<LimitValue limit={tenant.limits.cashiers} />} />
            <Row label="Сотрудники" value={<LimitValue limit={tenant.limits.users} />} />
          </>
        )}
      </dl>
    </section>
  );
}

/** «1 из 2 · тариф 1 + доп. 1». Занято больше лимита — подсвечиваем: новую кассу не привязать. */
function LimitValue({ limit }: { limit: LimitInfo }) {
  if (limit.total === null) {
    return <span>{limit.used} · без лимита</span>;
  }
  const over = limit.used > limit.total;
  return (
    <span className={over ? 'text-amber-700' : undefined}>
      {limit.used} из {limit.total}
      {limit.extra > 0 && (
        <span className="text-ink/50">
          {' '}· тариф {limit.plan ?? '∞'} + доп. {limit.extra}
        </span>
      )}
    </span>
  );
}

// ─── Profile card (inline edit) ─────────────────────────────────

function ProfileCard({
  tenant,
  onSaved,
  onTransferOwner,
  onRevokeOwnerSessions,
  ownerActionsBusy,
}: {
  tenant: TenantDetailDto;
  onSaved: (msg?: string) => Promise<void> | void;
  onTransferOwner: () => void;
  onRevokeOwnerSessions: () => void;
  ownerActionsBusy: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<UpdateTenantBody>(() => toDraft(tenant));

  useEffect(() => { setDraft(toDraft(tenant)); }, [tenant]);

  const networkSize = tenant.network.ownerEstablishmentCount;
  const emailChanged =
    (draft.ownerEmail ?? '').trim().toLowerCase() !== (tenant.ownerEmail ?? '').trim().toLowerCase();

  const save = async () => {
    setErr(null);
    const patch = diffDraft(draft, toDraft(tenant));
    if (patch.ownerEmail !== undefined) {
      const next = patch.ownerEmail.trim();
      if (!emailChanged) {
        delete patch.ownerEmail;
      } else if (!next) {
        setErr('Почту владельца нельзя оставить пустой');
        return;
      } else {
        patch.ownerEmail = next;
        const ok = window.confirm(
          `Сменить почту владельца на ${next}?\n\n` +
            (networkSize > 1
              ? `Почта — вход во все ${networkSize} ${pluralEstablishments(networkSize)} владельца: новый адрес получит всю сеть.`
              : 'Почта — вход владельца: новый адрес получит эту точку.') +
            ' Текущие сессии владельца завершатся.\n\nПродали одну точку — используйте «Передать точку другому владельцу».',
        );
        if (!ok) return;
      }
    }
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      const res = await apiUpdateTenant(tenant.id, patch);
      await onSaved(
        res.ownerEmailChanged ? `Почта владельца: ${res.ownerEmailChanged.to} · его сессии завершены` : undefined,
      );
      setEditing(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card p-6">
      <div className="flex items-center justify-between">
        <h2 className="eyebrow">Контакты</h2>
        {!editing ? (
          <button
            className="inline-flex items-center gap-1.5 text-[12px] text-ink/60 hover:text-ink"
            type="button"
            onClick={() => setEditing(true)}
          >
            <Pencil size={12} strokeWidth={1.75} />
            Редактировать
          </button>
        ) : (
          <div className="flex items-center gap-1">
            <button
              className="inline-flex items-center gap-1 text-[12px] text-ink/60 hover:text-ink"
              type="button"
              onClick={() => { setDraft(toDraft(tenant)); setEditing(false); setErr(null); }}
            >
              <X size={12} strokeWidth={1.75} />
              Отмена
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        <>
          <dl className="mt-4 space-y-3 text-[13px]">
            <Row label="Название" value={tenant.name || '—'} />
            <Row label="Владелец" value={tenant.ownerName ?? '—'} />
            <Row
              label="Email"
              value={
                tenant.ownerEmail ? (
                  <a href={`mailto:${tenant.ownerEmail}`} className="link">
                    {tenant.ownerEmail}
                  </a>
                ) : '—'
              }
            />
            <Row label="Телефон" value={tenant.ownerPhone ?? tenant.phone ?? '—'} />
            <Row label="Адрес" value={tenant.city ?? '—'} />
            <Row
              label="Домен"
              value={tenant.domain ? <span className="font-mono">{tenant.domain}</span> : '—'}
            />
            {tenant.network.ownerEstablishmentCount > 1 && (
              <Row
                label="Сеть владельца"
                value={
                  <span className="flex flex-col items-end gap-1.5">
                    {tenant.network.establishments.map((e) => (
                      <span key={e.id} className="inline-flex items-center gap-2">
                        {e.id === tenant.id ? (
                          <span className="text-ink">{e.name}</span>
                        ) : (
                          <Link to={`/tenants/${e.id}`} className="link">
                            {e.name}
                          </Link>
                        )}
                        <StatusBadge status={e.status} />
                      </span>
                    ))}
                  </span>
                }
              />
            )}
          </dl>
          <div className="mt-4 flex flex-col items-start gap-2 border-t border-line pt-3">
            <button
              className="inline-flex items-center gap-1.5 text-[12px] text-ink/60 hover:text-ink disabled:opacity-50"
              type="button"
              onClick={onTransferOwner}
              disabled={ownerActionsBusy}
            >
              <ArrowRightLeft size={12} strokeWidth={1.75} />
              {tenant.ownerId ? 'Передать точку другому владельцу' : 'Назначить владельца'}
            </button>
            {tenant.ownerId && (
              <button
                className="inline-flex items-center gap-1.5 text-[12px] text-ink/60 hover:text-ink disabled:opacity-50"
                type="button"
                onClick={onRevokeOwnerSessions}
                disabled={ownerActionsBusy}
              >
                <LogOut size={12} strokeWidth={1.75} />
                Завершить сессии владельца
              </button>
            )}
            {tenant.ownerSessionsRevokedAt && (
              <span className="text-[11px] text-ink/40">
                Сессии завершены {formatDateTime(tenant.ownerSessionsRevokedAt)}
              </span>
            )}
          </div>
        </>
      ) : (
        <div className="mt-4 space-y-3 text-[13px]">
          <FieldRow label="Название">
            <input
              className="input"
              value={draft.name ?? ''}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </FieldRow>
          <FieldRow label="Владелец">
            <input
              className="input"
              value={draft.ownerName ?? ''}
              onChange={(e) => setDraft({ ...draft, ownerName: e.target.value })}
            />
          </FieldRow>
          <FieldRow label="Email">
            {tenant.ownerId ? (
              <input
                className="input"
                type="email"
                autoComplete="off"
                value={draft.ownerEmail ?? ''}
                onChange={(e) => setDraft({ ...draft, ownerEmail: e.target.value })}
              />
            ) : (
              <input
                className="input bg-paper text-ink/60"
                value={tenant.ownerEmail ?? ''}
                disabled
                title="У точки нет владельца — назначьте его в карточке"
              />
            )}
            {emailChanged && (
              <div className="mt-2 flex items-start gap-2 border border-amber-500/40 bg-amber-50 px-3 py-2.5 text-[12px] leading-relaxed text-amber-800">
                <AlertTriangle size={14} strokeWidth={1.75} className="mt-0.5 shrink-0" />
                <span>
                  {networkSize > 1
                    ? `Почта — вход во все ${networkSize} ${pluralEstablishments(networkSize)} владельца: новый адрес получит всю сеть.`
                    : 'Почта — вход владельца: новый адрес получит эту точку.'}{' '}
                  Сессии владельца завершатся. Продали одну точку — «Передать точку другому
                  владельцу».
                </span>
              </div>
            )}
          </FieldRow>
          <FieldRow label="Телефон">
            <input
              className="input"
              value={draft.ownerPhone ?? ''}
              onChange={(e) => setDraft({ ...draft, ownerPhone: e.target.value })}
            />
          </FieldRow>
          <FieldRow label="Адрес">
            <input
              className="input"
              value={draft.address ?? ''}
              onChange={(e) => setDraft({ ...draft, address: e.target.value })}
            />
          </FieldRow>
          <FieldRow label="Домен меню">
            <input
              className="input font-mono"
              placeholder="menu.example.ru"
              value={draft.domain ?? ''}
              onChange={(e) => setDraft({ ...draft, domain: e.target.value })}
            />
          </FieldRow>

          {err && <p className="text-[12px] text-red-600">{err}</p>}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              className="btn-ghost"
              type="button"
              onClick={() => { setDraft(toDraft(tenant)); setEditing(false); setErr(null); }}
              disabled={busy}
            >
              Отмена
            </button>
            <button className="btn-dark" type="button" onClick={save} disabled={busy}>
              <Check size={14} strokeWidth={1.75} />
              Сохранить
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function toDraft(t: TenantDetailDto): UpdateTenantBody {
  return {
    name: t.name ?? '',
    address: t.city ?? '',
    phone: t.phone ?? '',
    domain: t.domain ?? '',
    ownerName: t.ownerName ?? '',
    ownerPhone: t.ownerPhone ?? '',
    ownerEmail: t.ownerEmail ?? '',
  };
}

function diffDraft(a: UpdateTenantBody, b: UpdateTenantBody): UpdateTenantBody {
  const patch: UpdateTenantBody = {};
  (Object.keys(a) as (keyof UpdateTenantBody)[]).forEach((k) => {
    const av = a[k];
    const bv = b[k];
    if ((av ?? '') !== (bv ?? '')) {
      // Пустую строку в домене отправляем как null — чтобы бэкенд отвязал домен.
      if (k === 'domain' && (av === '' || av == null)) {
        (patch as Record<string, unknown>)[k] = null;
      } else {
        (patch as Record<string, unknown>)[k] = av;
      }
    }
  });
  return patch;
}

// ─── Usage card ─────────────────────────────────────────────────

function UsageCard({
  tenant,
  avgOrder,
}: {
  tenant: TenantDetailDto;
  avgOrder: number | null;
}) {
  return (
    <section className="card p-6">
      <h2 className="eyebrow">30 дней</h2>
      <div className="mt-4 grid grid-cols-2 gap-4">
        <div>
          <div className="kpi-value">{formatKop(tenant.usage.revenueLast30d)}</div>
          <div className="mt-2 text-[11px] text-ink/50">Оборот</div>
        </div>
        <div>
          <div className="kpi-value">{formatNumber(tenant.usage.ordersLast30d)}</div>
          <div className="mt-2 text-[11px] text-ink/50">Заказов</div>
        </div>
        <div>
          <div className="kpi-value">
            {tenant.usage.devicesOnline}
            <span className="text-[14px] font-normal text-ink/40">/{tenant.usage.devicesTotal}</span>
          </div>
          <div className="mt-2 text-[11px] text-ink/50">Устройств онлайн</div>
        </div>
        <div>
          <div className="kpi-value">{avgOrder ? formatKop(avgOrder) : '—'}</div>
          <div className="mt-2 text-[11px] text-ink/50">Средний чек</div>
        </div>
      </div>
    </section>
  );
}

// ─── Referrer (менеджер) ───────────────────────────────────────

function ReferrerSection({
  tenantId,
  tenant,
  onChanged,
}: {
  tenantId: string;
  tenant: TenantDetailDto;
  onChanged: () => Promise<void> | void;
}) {
  const [options, setOptions] = useState<Array<{ id: string; label: string }>>([]);
  const [selected, setSelected] = useState<string>(tenant.referrer?.id ?? '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listManagers({ status: 'active', limit: 200 })
      .then((r) =>
        setOptions(
          r.items.map((m) => ({
            id: m.id,
            label: `${m.name} (${m.code})`,
          })),
        ),
      )
      .catch(() => {});
  }, []);

  useEffect(() => {
    setSelected(tenant.referrer?.id ?? '');
  }, [tenant.referrer?.id]);

  const save = async (managerId: string | null) => {
    setBusy(true);
    try {
      await assignTenantReferrer(tenantId, managerId);
      await onChanged();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Ошибка');
      setSelected(tenant.referrer?.id ?? '');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mx-4 mb-4 border border-line bg-white px-4 py-5 md:mx-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-[15px] font-medium text-ink">Реферальный менеджер</h2>
          <p className="mt-1 text-[12px] text-ink/55">
            20% (или индивидуальная ставка) с каждого успешного платежа подписки этого заведения.
          </p>
          {tenant.referrer ? (
            <div className="mt-2 text-[13px]">
              <Link to={`/managers/${tenant.referrer.id}`} className="link font-medium">
                {tenant.referrer.name}
              </Link>
              <span className="text-ink/45"> · код {tenant.referrer.code}</span>
              {tenant.referrer.attachedAt ? (
                <span className="text-ink/45"> · с {formatDate(tenant.referrer.attachedAt)}</span>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="flex min-w-[240px] flex-col gap-2 sm:max-w-xs sm:flex-1">
          <select
            className="input"
            value={selected}
            disabled={busy}
            onChange={(e) => {
              const v = e.target.value;
              setSelected(v);
              void save(v || null);
            }}
          >
            <option value="">— не привязан —</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </section>
  );
}

// ─── Add-ons section ────────────────────────────────────────────

function AddonsSection({
  tenantId,
  tenant,
  onAttach,
  onChanged,
}: {
  tenantId: string;
  tenant: TenantDetailDto;
  onAttach: () => void;
  onChanged: (msg: string) => Promise<void> | void;
}) {
  const addons = tenant.subscription?.addons ?? [];
  const [busyCode, setBusyCode] = useState<string | null>(null);
  // Отключённые, но дорабатывающие срок модули в сумму следующего списания не входят.
  const monthlyTotal = addons
    .filter((a) => a.renewing !== false)
    .reduce((sum, a) => sum + a.priceMonthKopecks * a.quantity, 0);

  const handleDetach = async (addon: (typeof addons)[number]) => {
    const ending = addon.renewing === false;
    const question = ending
      ? `Отключить сейчас? «${addon.name}» перестанет работать сразу, не дожидаясь ${
          addon.activeUntil ? formatDate(addon.activeUntil) : 'конца срока'
        }.`
      : 'Отключить модуль? Списания прекратятся с начала следующего периода.';
    if (!window.confirm(question)) return;
    try {
      setBusyCode(addon.code);
      await detachTenantAddon(tenantId, addon.code);
      await onChanged('Модуль отключён');
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setBusyCode(null);
    }
  };

  // Модуль отключён и дорабатывает срок — подключаем снова, с продлением.
  const handleResume = async (addon: (typeof addons)[number]) => {
    try {
      setBusyCode(addon.code);
      await attachTenantAddon(tenantId, addon.code);
      await onChanged(`«${addon.name}» снова подключён`);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setBusyCode(null);
    }
  };

  const handleQuantity = async (addon: (typeof addons)[number], quantity: number) => {
    // Лимит проверяется только при привязке, поэтому уменьшение ниже занятого не
    // отключит работающие кассы — но оператор должен понимать, что новую не привязать.
    const limit = addonLimit(tenant, addon.code);
    if (limit && limit.total !== null && quantity < addon.quantity) {
      const nextTotal = limit.total - (addon.quantity - quantity);
      if (
        limit.used > nextTotal &&
        !window.confirm(
          `Сейчас занято ${limit.used}, лимит станет ${nextTotal}. Работающие не отключатся, ` +
            'но новых не добавить, пока занято больше лимита. Продолжить?',
        )
      ) {
        return;
      }
    }
    try {
      setBusyCode(addon.code);
      await setTenantAddonQuantity(tenantId, addon.code, quantity);
      await onChanged(`«${addon.name}»: ${quantity} шт.`);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setBusyCode(null);
    }
  };

  return (
    <div className="px-4 pb-6 md:px-8">
      <div className="card">
        <div className="flex flex-col gap-3 border-b border-line px-4 py-4 md:flex-row md:items-center md:justify-between md:px-5">
          <div>
            <h2 className="text-[14px] font-medium">
              Подключённые модули ({addons.length})
              {monthlyTotal > 0 && (
                <span className="font-normal text-ink/50"> · {formatKop(monthlyTotal)} / мес</span>
              )}
            </h2>
            <p className="mt-1 text-[12px] text-ink/50">
              Платные дополнения сверх тарифа. Цена фиксируется на момент подключения и входит в
              сумму подписки: оплата из кабинета, автопродление и «Оплата наличными».
              «Дополнительная касса» и «Дополнительный пользователь» подключаются штуками и сразу
              поднимают лимит тарифа.
            </p>
          </div>
          <button className="btn-outline w-full md:w-auto" type="button" onClick={onAttach}>
            <Plus size={14} strokeWidth={1.75} />
            Подключить модуль
          </button>
        </div>
        {addons.length === 0 ? (
          <div className="px-5 py-8 text-center text-[13px] text-ink/50">
            Модули не подключены.
          </div>
        ) : (
          <>
            {/* Desktop */}
            <table className="table-grid hidden md:table">
              <thead>
                <tr>
                  <th>Модуль</th>
                  <th>Код</th>
                  <th>Количество</th>
                  <th className="text-right">Цена / мес</th>
                  <th className="text-right">Подключён</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {addons.map((a) => (
                  <tr key={a.id}>
                    <td className="font-medium">{a.name}</td>
                    <td className="font-mono text-[12px] text-ink/60">{a.code}</td>
                    <td>
                      <AddonQuantity
                        addon={a}
                        busy={busyCode === a.code}
                        onChange={(q) => handleQuantity(a, q)}
                      />
                    </td>
                    <td className="text-right font-mono">
                      {formatKop(a.priceMonthKopecks * a.quantity)}
                      {a.quantity > 1 && (
                        <div className="text-[11px] text-ink/50">
                          {formatKop(a.priceMonthKopecks)} × {a.quantity}
                        </div>
                      )}
                      {a.renewing === false && (
                        <div className="font-sans text-[11px] text-amber-700">не продлевается</div>
                      )}
                    </td>
                    <td className="text-right font-mono text-[12px] text-ink/60">
                      {formatDate(a.activeFrom)}
                      {a.renewing === false && a.activeUntil && (
                        <div className="font-sans text-[11px] text-amber-700">
                          до {formatDate(a.activeUntil)}
                        </div>
                      )}
                    </td>
                    <td className="text-right">
                      {a.renewing === false && (
                        <button
                          className="mr-3 inline-flex items-center gap-1 text-[12px] text-ink hover:underline disabled:opacity-50"
                          type="button"
                          onClick={() => handleResume(a)}
                          disabled={busyCode === a.code}
                        >
                          <RotateCcw size={12} strokeWidth={1.75} />
                          Возобновить
                        </button>
                      )}
                      <button
                        className="inline-flex items-center gap-1 text-[12px] text-red-600 hover:underline disabled:opacity-50"
                        type="button"
                        onClick={() => handleDetach(a)}
                        disabled={busyCode === a.code}
                      >
                        <Trash2 size={12} strokeWidth={1.75} />
                        {a.renewing === false ? 'Отключить сейчас' : 'Отключить'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile */}
            <ul className="divide-y divide-line md:hidden">
              {addons.map((a) => (
                <li key={a.id} className="px-4 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-[14px] font-medium text-ink">{a.name}</div>
                      <div className="mt-0.5 font-mono text-[11px] text-ink/50">{a.code}</div>
                      <div className="mt-0.5 font-mono text-[11px] text-ink/50">
                        Подключён {formatDate(a.activeFrom)}
                      </div>
                      {a.renewing === false && a.activeUntil && (
                        <div className="mt-0.5 text-[11px] text-amber-700">
                          Работает до {formatDate(a.activeUntil)}, не продлевается
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 text-right font-mono text-[13px]">
                      {formatKop(a.priceMonthKopecks * a.quantity)}
                      <div className="text-[10px] font-sans text-ink/50">
                        {a.quantity > 1 ? `${formatKop(a.priceMonthKopecks)} × ${a.quantity} / мес` : '/ мес'}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <AddonQuantity
                      addon={a}
                      busy={busyCode === a.code}
                      onChange={(q) => handleQuantity(a, q)}
                    />
                    <div className="flex items-center gap-3">
                      {a.renewing === false && (
                        <button
                          className="inline-flex items-center gap-1 text-[12px] font-medium text-ink hover:underline disabled:opacity-50"
                          type="button"
                          onClick={() => handleResume(a)}
                          disabled={busyCode === a.code}
                        >
                          <RotateCcw size={12} strokeWidth={1.75} />
                          Возобновить
                        </button>
                      )}
                      <button
                        className="inline-flex items-center gap-1 text-[12px] font-medium text-red-600 hover:underline disabled:opacity-50"
                        type="button"
                        onClick={() => handleDetach(a)}
                        disabled={busyCode === a.code}
                      >
                        <Trash2 size={12} strokeWidth={1.75} />
                        {a.renewing === false ? 'Отключить сейчас' : 'Отключить'}
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

/** Какой лимит поднимает «штучный» модуль. */
function addonLimit(tenant: TenantDetailDto, code: string): LimitInfo | null {
  if (!tenant.limits) return null;
  if (code === 'extra_cashier') return tenant.limits.cashiers;
  if (code === 'extra_user') return tenant.limits.users;
  return null;
}

/** Количество: у «штучных» модулей — степпер, у остальных — просто «1». */
function AddonQuantity({
  addon,
  busy,
  onChange,
}: {
  addon: { quantity: number; maxQuantity: number | null; name: string };
  busy: boolean;
  onChange: (quantity: number) => void;
}) {
  if (!addon.maxQuantity) {
    return <span className="font-mono text-[12px] text-ink/50">1</span>;
  }
  const stepClass =
    'inline-flex h-7 w-7 items-center justify-center border border-line text-ink/70 hover:border-ink hover:text-ink disabled:cursor-not-allowed disabled:opacity-40';
  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        type="button"
        className={stepClass}
        onClick={() => onChange(addon.quantity - 1)}
        disabled={busy || addon.quantity <= 1}
        aria-label={`Уменьшить «${addon.name}»`}
      >
        <Minus size={12} strokeWidth={1.75} />
      </button>
      <span className="min-w-[1.5rem] text-center font-mono text-[13px] tabular-nums">
        {addon.quantity}
      </span>
      <button
        type="button"
        className={stepClass}
        onClick={() => onChange(addon.quantity + 1)}
        disabled={busy || addon.quantity >= addon.maxQuantity}
        aria-label={`Увеличить «${addon.name}»`}
      >
        <Plus size={12} strokeWidth={1.75} />
      </button>
    </div>
  );
}

// ─── Devices section ────────────────────────────────────────────

function DevicesSection({
  tenant,
  onRevoke,
  onRefresh,
  actionBusy,
}: {
  tenant: TenantDetailDto;
  onRevoke: (deviceId: string) => Promise<void> | void;
  onRefresh: () => void;
  actionBusy: boolean;
}) {
  return (
    <div className="px-4 pb-6 md:px-8">
      <div className="card">
        <div className="flex items-center justify-between border-b border-line px-4 py-4 md:px-5">
          <h2 className="text-[14px] font-medium">Устройства ({tenant.devices.length})</h2>
          <button className="btn-ghost" type="button" onClick={onRefresh} disabled={actionBusy}>
            <RefreshCw size={13} strokeWidth={1.75} />
            Обновить
          </button>
        </div>

        {/* Desktop */}
        <table className="table-grid hidden md:table">
          <thead>
            <tr>
              <th>Имя</th>
              <th>Платформа</th>
              <th>Статус</th>
              <th>IP</th>
              <th className="text-right">Последняя активность</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tenant.devices.map((d) => {
              const isOnline = !d.revoked && d.lastSeenAt
                && (Date.now() - new Date(d.lastSeenAt).getTime()) < 5 * 60 * 1000;
              return (
                <tr key={d.id}>
                  <td className="font-medium">{d.name}</td>
                  <td className="text-ink/70">{d.platform}</td>
                  <td>
                    {d.revoked ? (
                      <span className="text-[12px] text-red-600">Отозвано</span>
                    ) : isOnline ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-green-600" />
                        <span className="text-[12px]">Онлайн</span>
                      </span>
                    ) : (
                      <span className="text-[12px] text-ink/50">Оффлайн</span>
                    )}
                  </td>
                  <td className="font-mono text-[12px] text-ink/70">{d.lastIp ?? '—'}</td>
                  <td className="text-right font-mono text-[12px] text-ink/60">
                    {d.lastSeenAt ? relativeTime(d.lastSeenAt) : '—'}
                  </td>
                  <td className="text-right">
                    {!d.revoked && (
                      <button
                        className="text-[12px] text-red-600 hover:underline"
                        onClick={() => onRevoke(d.id)}
                        disabled={actionBusy}
                      >
                        Отозвать
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {tenant.devices.length === 0 && (
              <tr>
                <td colSpan={6} className="py-6 text-center text-ink/50">
                  Устройства не подключены
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Mobile */}
        <ul className="divide-y divide-line md:hidden">
          {tenant.devices.map((d) => {
            const isOnline = !d.revoked && d.lastSeenAt
              && (Date.now() - new Date(d.lastSeenAt).getTime()) < 5 * 60 * 1000;
            return (
              <li key={d.id} className="px-4 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-[14px] font-medium text-ink">{d.name}</div>
                    <div className="mt-0.5 text-[12px] text-ink/60">{d.platform}</div>
                  </div>
                  <div className="shrink-0">
                    {d.revoked ? (
                      <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-red-600">
                        Отозвано
                      </span>
                    ) : isOnline ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-green-600" />
                        <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-green-700">
                          Онлайн
                        </span>
                      </span>
                    ) : (
                      <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink/50">
                        Оффлайн
                      </span>
                    )}
                  </div>
                </div>
                <dl className="mt-2 grid grid-cols-2 gap-y-1.5 text-[12px]">
                  <dt className="text-ink/50">IP</dt>
                  <dd className="text-right font-mono text-ink/60">{d.lastIp ?? '—'}</dd>
                  <dt className="text-ink/50">Активность</dt>
                  <dd className="text-right font-mono text-ink/60">
                    {d.lastSeenAt ? relativeTime(d.lastSeenAt) : '—'}
                  </dd>
                </dl>
                {!d.revoked && (
                  <div className="mt-2 flex justify-end">
                    <button
                      className="text-[12px] font-medium text-red-600 hover:underline"
                      onClick={() => onRevoke(d.id)}
                      disabled={actionBusy}
                    >
                      Отозвать
                    </button>
                  </div>
                )}
              </li>
            );
          })}
          {tenant.devices.length === 0 && (
            <li className="px-4 py-6 text-center text-[13px] text-ink/50">
              Устройства не подключены
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

// ─── Payments section ───────────────────────────────────────────

function PaymentsSection({ tenant }: { tenant: TenantDetailDto }) {
  return (
    <div className="px-4 pb-10 md:px-8">
      <div className="card">
        <div className="border-b border-line px-4 py-4 md:px-5">
          <h2 className="text-[14px] font-medium">История платежей</h2>
        </div>

        {/* Desktop */}
        <table className="table-grid hidden md:table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Метод</th>
              <th>Статус</th>
              <th className="text-right">Сумма</th>
              <th className="text-right">Дата</th>
            </tr>
          </thead>
          <tbody>
            {tenant.payments.map((p) => (
              <tr key={p.id}>
                <td className="font-mono text-[12px]">{p.id.slice(0, 8)}</td>
                <td>{p.method ?? '—'}</td>
                <td>
                  <span className={paymentBadgeClass(p.status)}>
                    {paymentStatusLabel(p.status)}
                  </span>
                </td>
                <td className="text-right font-mono">{formatKop(p.amount)}</td>
                <td className="text-right font-mono text-[12px] text-ink/60">
                  {formatDateTime(p.createdAt)}
                </td>
              </tr>
            ))}
            {tenant.payments.length === 0 && (
              <tr>
                <td colSpan={5} className="py-6 text-center text-ink/50">
                  Платежей пока нет
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Mobile */}
        <ul className="divide-y divide-line md:hidden">
          {tenant.payments.map((p) => (
            <li key={p.id} className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono text-[12px] text-ink">{p.id.slice(0, 8)}</div>
                  <div className="mt-0.5 text-[12px] text-ink/60">{p.method ?? '—'}</div>
                  <div className="mt-0.5 font-mono text-[11px] text-ink/50">
                    {formatDateTime(p.createdAt)}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-mono text-[13px] font-medium">{formatKop(p.amount)}</div>
                  <span className={`${paymentBadgeClass(p.status)} mt-1`}>
                    {paymentStatusLabel(p.status)}
                  </span>
                </div>
              </div>
            </li>
          ))}
          {tenant.payments.length === 0 && (
            <li className="px-4 py-6 text-center text-[13px] text-ink/50">Платежей пока нет</li>
          )}
        </ul>
      </div>
    </div>
  );
}

// ─── Danger zone ────────────────────────────────────────────────

function DangerZone({
  onDelete,
  disabled,
}: {
  onDelete: () => void;
  disabled: boolean;
}) {
  return (
    <div className="px-4 pb-12 md:px-8">
      <div className="card border-red-600/30">
        <div className="flex flex-col gap-3 border-b border-red-600/20 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-5">
          <div className="flex items-start gap-2.5">
            <AlertTriangle size={18} strokeWidth={1.75} className="mt-0.5 shrink-0 text-red-600" />
            <div>
              <h2 className="text-[14px] font-medium text-red-700">Опасная зона</h2>
              <p className="mt-1 text-[12px] text-ink/50">
                Полное удаление аккаунта снесёт заведение, всё меню, склад, заказы, смены,
                устройства, биллинг и всех пользователей. Действие необратимо.
              </p>
            </div>
          </div>
          <button
            className="btn-danger w-full shrink-0 md:w-auto"
            type="button"
            onClick={onDelete}
            disabled={disabled}
          >
            <Trash2 size={14} strokeWidth={1.75} />
            Удалить аккаунт
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Передать точку другому владельцу ─────────────────────────────
// Продажа одной точки сети: смена почты владельца отдала бы всю сеть.

function DetachOwnerModal({
  tenant,
  onClose,
  onDone,
}: {
  tenant: TenantDetailDto;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [revokePrevious, setRevokePrevious] = useState(true);
  const [clearCard, setClearCard] = useState(true);
  const [clearYukassa, setClearYukassa] = useState(true);
  const [existing, setExisting] = useState<ExistingOwnerInfo['owner'] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const hasOwner = !!tenant.ownerId;
  const seller = tenant.ownerName || tenant.ownerEmail || 'Владелец';
  const others = tenant.network.establishments.filter((e) => e.id !== tenant.id);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const sellerPayDetails = [
    tenant.guestPayments.transfer ? 'перевод' : null,
    tenant.guestPayments.qr ? 'QR-код' : null,
  ].filter(Boolean);

  const submit = async (attachToExisting: boolean) => {
    if (!emailValid || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await detachTenantOwner(tenant.id, {
        email: email.trim(),
        name: name.trim() || null,
        phone: phone.trim() || null,
        attachToExisting,
        revokePreviousOwnerSessions: hasOwner ? revokePrevious : undefined,
        clearSavedCard: tenant.subscription?.savedCard ? clearCard : undefined,
        clearYukassa: tenant.guestPayments.yukassa ? clearYukassa : undefined,
      });
      onDone(
        res.newOwner.mode === 'attached'
          ? `«${tenant.name}» теперь в сети ${res.newOwner.email}`
          : `«${tenant.name}» передана ${res.newOwner.email}`,
      );
    } catch (e) {
      if (e instanceof ApiError && e.code === 'OWNER_EXISTS') {
        const info = e.details as ExistingOwnerInfo | undefined;
        setExisting(info?.owner ?? { id: '', name: null, email: email.trim(), establishments: 0 });
      } else {
        setErr(e instanceof Error ? e.message : 'Ошибка');
      }
      setBusy(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      title={hasOwner ? 'Передать точку другому владельцу' : 'Назначить владельца'}
    >
      <div className="space-y-4">
        <p className="text-[13px] leading-relaxed text-ink/70">
          <span className="font-medium text-ink">«{tenant.name}»</span> уйдёт новому владельцу целиком:
          меню, сотрудники, кассы, заказы, подписка.
          {hasOwner &&
            (others.length > 0 ? (
              <> У прежнего владельца ({seller}) останутся: {others.map((e) => e.name).join(', ')}.</>
            ) : (
              <> Других точек у прежнего владельца ({seller}) нет — аккаунт останется без заведений.</>
            ))}
        </p>

        <FieldRow label="Почта нового владельца">
          <input
            className="input"
            type="email"
            autoComplete="off"
            autoFocus
            placeholder="buyer@example.ru"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setExisting(null);
            }}
          />
          <p className="mt-1 text-[11px] text-ink/50">
            Вход — по коду на эту почту, в админку и личный кабинет.
          </p>
        </FieldRow>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FieldRow label="Имя">
            <input
              className="input"
              placeholder="Необязательно"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </FieldRow>
          <FieldRow label="Телефон">
            <input
              className="input"
              type="tel"
              placeholder="+7…"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </FieldRow>
        </div>

        {(hasOwner || tenant.subscription?.savedCard || tenant.guestPayments.yukassa) && (
          <div className="space-y-2.5 border-t border-line pt-3">
            {hasOwner && (
              <label className="flex items-start gap-2 text-[13px]">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-ink"
                  checked={revokePrevious}
                  onChange={(e) => setRevokePrevious(e.target.checked)}
                />
                <span>
                  Завершить сессии прежнего владельца
                  <span className="block text-[11px] text-ink/50">
                    {seller} выйдет из админки и ЛК на всех устройствах — в том числе на планшете
                    в этой точке, если там вход под ним. Войдёт заново по коду.
                  </span>
                </span>
              </label>
            )}
            {tenant.subscription?.savedCard && (
              <label className="flex items-start gap-2 text-[13px]">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-ink"
                  checked={clearCard}
                  onChange={(e) => setClearCard(e.target.checked)}
                />
                <span>
                  Отвязать карту продавца от подписки
                  <span className="block text-[11px] text-ink/50">
                    Иначе продление этой точки спишется с его карты.
                  </span>
                </span>
              </label>
            )}
            {tenant.guestPayments.yukassa && (
              <label className="flex items-start gap-2 text-[13px]">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-ink"
                  checked={clearYukassa}
                  onChange={(e) => setClearYukassa(e.target.checked)}
                />
                <span>
                  Отключить ЮKassa продавца
                  <span className="block text-[11px] text-ink/50">
                    Иначе онлайн-оплаты гостей этой точки пойдут в его магазин.
                  </span>
                </span>
              </label>
            )}
          </div>
        )}

        {sellerPayDetails.length > 0 && (
          <div className="flex items-start gap-2 border border-amber-500/40 bg-amber-50 px-3 py-2.5 text-[12px] leading-relaxed text-amber-800">
            <AlertTriangle size={14} strokeWidth={1.75} className="mt-0.5 shrink-0" />
            <span>
              В оплате включены {sellerPayDetails.join(' и ')} — там реквизиты продавца. Новому
              владельцу нужно заменить их в админке.
            </span>
          </div>
        )}

        {existing && (
          <div className="flex items-start gap-2 border border-amber-500/40 bg-amber-50 px-3 py-2.5 text-[12px] leading-relaxed text-amber-800">
            <AlertTriangle size={14} strokeWidth={1.75} className="mt-0.5 shrink-0" />
            <span>
              У {existing.email} уже есть владелец
              {existing.name ? ` «${existing.name}»` : ''}
              {existing.establishments > 0
                ? ` — ${existing.establishments} ${pluralEstablishments(existing.establishments)}`
                : ''}
              . Точка войдёт в его сеть, вход — по этой же почте.
            </span>
          </div>
        )}

        {err && <p className="text-[12px] text-red-600">{err}</p>}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button className="btn-ghost" type="button" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button
            className="btn-dark"
            type="button"
            onClick={() => submit(!!existing)}
            disabled={busy || !emailValid}
          >
            <ArrowRightLeft size={14} strokeWidth={1.75} />
            {busy ? 'Передаю…' : existing ? 'Передать в его сеть' : 'Передать точку'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DeleteTenantModal({
  tenantId,
  tenantName,
  ownerEmail,
  ownerStays,
  usersCount,
  onClose,
  onDeleted,
}: {
  tenantId: string;
  tenantName: string;
  ownerEmail: string | null;
  /** Владелец не удаляется: домашняя точка у него другая или есть другие точки. */
  ownerStays: boolean;
  usersCount: number;
  onClose: () => void;
  onDeleted: (name: string) => void;
}) {
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const matches = confirm.trim() === tenantName.trim();

  const submit = async () => {
    if (!matches) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await apiDeleteTenant(tenantId);
      onDeleted(res.name ?? tenantName);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Ошибка удаления');
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Удаление аккаунта">
      <div className="space-y-4">
        <div className="flex items-start gap-2.5 border border-red-600/30 bg-red-50 px-3 py-2.5">
          <AlertTriangle size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-red-600" />
          <p className="text-[12px] leading-relaxed text-red-700">
            Это <strong>необратимо</strong>. Будут безвозвратно удалены заведение
            <strong> «{tenantName}»</strong>, все его данные (меню, склад, заказы, смены,
            устройства, история биллинга) и <strong>{usersCount}</strong>{' '}
            {usersCount === 1 ? 'пользователь' : usersCount < 5 ? 'пользователя' : 'пользователей'}
            {ownerEmail && !ownerStays ? <> (включая владельца {ownerEmail})</> : null}.
            {ownerEmail && ownerStays ? <> Владелец {ownerEmail} не удаляется.</> : null}
          </p>
        </div>

        <div>
          <label className="eyebrow">
            Для подтверждения введите название заведения
          </label>
          <p className="mt-1 select-all font-mono text-[13px] text-ink">{tenantName}</p>
          <input
            className="input mt-2"
            value={confirm}
            autoFocus
            placeholder="Название заведения"
            onChange={(e) => setConfirm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && matches && !busy) submit();
            }}
          />
        </div>

        {err && <p className="text-[12px] text-red-600">{err}</p>}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button className="btn-ghost" type="button" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button
            className="btn-danger"
            type="button"
            onClick={submit}
            disabled={busy || !matches}
          >
            <Trash2 size={14} strokeWidth={1.75} />
            {busy ? 'Удаляю…' : 'Удалить навсегда'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Trial modal ────────────────────────────────────────────────

function TrialModal({
  tenantId,
  currentTrialEnd,
  onClose,
  onSaved,
}: {
  tenantId: string;
  currentTrialEnd: string | null;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [mode, setMode] = useState<'days' | 'until'>('days');
  const [days, setDays] = useState<number>(7);
  const [until, setUntil] = useState<string>(
    currentTrialEnd
      ? currentTrialEnd.slice(0, 10)
      : new Date(Date.now() + 7 * 86400_000).toISOString().slice(0, 10),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      if (mode === 'days') {
        if (!Number.isInteger(days) || days <= 0 || days > 3650) {
          throw new Error('Введите число от 1 до 3650');
        }
        await adjustTenantTrial(tenantId, { days });
      } else {
        if (!until) throw new Error('Выберите дату');
        await adjustTenantTrial(tenantId, { until: new Date(until + 'T23:59:59Z').toISOString() });
      }
      await onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Продление trial">
      <div className="space-y-4">
        <p className="text-[13px] text-ink/60">
          Сейчас trial до{' '}
          <strong className="text-ink">
            {currentTrialEnd ? formatDate(currentTrialEnd) : 'не установлен'}
          </strong>
          .
        </p>

        <div className="inline-flex border border-line">
          <button
            type="button"
            className={`px-3 py-1.5 text-[12px] ${mode === 'days' ? 'bg-ink text-white' : 'text-ink/70'}`}
            onClick={() => setMode('days')}
          >
            Прибавить дни
          </button>
          <button
            type="button"
            className={`px-3 py-1.5 text-[12px] ${mode === 'until' ? 'bg-ink text-white' : 'text-ink/70'}`}
            onClick={() => setMode('until')}
          >
            До даты
          </button>
        </div>

        {mode === 'days' ? (
          <div>
            <label className="eyebrow">Прибавить дней</label>
            <input
              className="input mt-2"
              type="number"
              min={1}
              max={3650}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            />
            <div className="mt-2 flex flex-wrap gap-1">
              {[7, 14, 30, 60, 90].map((n) => (
                <button
                  key={n}
                  type="button"
                  className="border border-line px-2 py-1 text-[11px] text-ink/70 hover:border-ink hover:text-ink"
                  onClick={() => setDays(n)}
                >
                  +{n}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <label className="eyebrow">Дата окончания trial</label>
            <input
              className="input mt-2 font-mono"
              type="date"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
            />
          </div>
        )}

        {err && <p className="text-[12px] text-red-600">{err}</p>}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button className="btn-ghost" type="button" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button className="btn-primary" type="button" onClick={submit} disabled={busy}>
            Применить
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Уменьшение срока ───────────────────────────────────────────
// Режет конец доступа (триал или оплаченный период) на N дней или до даты.
// Статус не меняется: если новый конец уже прошёл, дальше обычный путь — грейс,
// PAST_DUE, а при сохранённой карте автоплатёж в новую дату.

// Грейс после конца срока — как SUBSCRIPTION_GRACE_DAYS на бэкенде; здесь только
// для подсказки в превью, реальное состояние вернёт сервер.
const GRACE_DAYS = 3;
const DAY_MS = 86_400_000;

function toDateInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function ReduceAccessModal({
  tenantId,
  sub,
  onClose,
  onSaved,
}: {
  tenantId: string;
  sub: NonNullable<TenantDetailDto['subscription']>;
  onClose: () => void;
  onSaved: (accessEndsAt: string | null) => Promise<void> | void;
}) {
  const currentEnd = useMemo(() => (sub.accessEndsAt ? new Date(sub.accessEndsAt) : null), [sub.accessEndsAt]);
  // Последний допустимый день для «До даты» — накануне текущего окончания.
  const maxDate = useMemo(() => {
    if (!currentEnd) return '';
    const d = new Date(currentEnd);
    d.setDate(d.getDate() - 1);
    return toDateInput(d);
  }, [currentEnd]);

  const [mode, setMode] = useState<'days' | 'until'>('days');
  const [days, setDays] = useState<number>(1);
  const [until, setUntil] = useState<string>(maxDate);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isTrial = sub.status === 'trial';
  const daysValid = Number.isInteger(days) && days >= 1 && days <= 3650;

  let next: Date | null = null;
  if (currentEnd && mode === 'days' && daysValid) {
    next = new Date(currentEnd);
    next.setDate(next.getDate() - days);
  } else if (currentEnd && mode === 'until' && until) {
    next = new Date(`${until}T23:59:59`);
  }
  const nextValid = !!next && !!currentEnd && next.getTime() < currentEnd.getTime();
  const now = Date.now();
  const nextDaysLeft = next ? Math.max(0, Math.ceil((next.getTime() - now) / DAY_MS)) : null;
  const expired = !!next && next.getTime() <= now;
  const blockAt = next ? new Date(next.getTime() + GRACE_DAYS * DAY_MS) : null;

  const submit = async () => {
    if (!next || !nextValid) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await reduceTenantAccess(
        tenantId,
        mode === 'days' ? { days } : { until: next.toISOString() },
      );
      await onSaved(res.accessEndsAt);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Ошибка');
      setBusy(false);
    }
  };

  if (!currentEnd) return null;

  return (
    <Modal onClose={onClose} title="Уменьшить срок">
      <div className="space-y-4">
        <p className="text-[13px] text-ink/60">
          Сейчас {isTrial ? 'trial' : 'оплачено'} до{' '}
          <strong className="text-ink">{formatDate(currentEnd.toISOString())}</strong>
          {sub.daysLeft != null && sub.accessState === 'ok' && (
            <> · осталось {sub.daysLeft} {pluralDays(sub.daysLeft)}</>
          )}
          .
        </p>

        <div className="inline-flex border border-line">
          <button
            type="button"
            className={`px-3 py-1.5 text-[12px] ${mode === 'days' ? 'bg-ink text-white' : 'text-ink/70'}`}
            onClick={() => setMode('days')}
          >
            Убавить дни
          </button>
          <button
            type="button"
            className={`px-3 py-1.5 text-[12px] ${mode === 'until' ? 'bg-ink text-white' : 'text-ink/70'}`}
            onClick={() => setMode('until')}
          >
            До даты
          </button>
        </div>

        {mode === 'days' ? (
          <div>
            <label className="eyebrow">Убавить дней</label>
            <input
              className="input mt-2"
              type="number"
              min={1}
              max={3650}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            />
            <div className="mt-2 flex flex-wrap gap-1">
              {[1, 3, 7, 14, 30].map((n) => (
                <button
                  key={n}
                  type="button"
                  className="border border-line px-2 py-1 text-[11px] text-ink/70 hover:border-ink hover:text-ink"
                  onClick={() => setDays(n)}
                >
                  −{n}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <label className="eyebrow">Новая дата окончания</label>
            <input
              className="input mt-2 font-mono"
              type="date"
              max={maxDate}
              value={until}
              onChange={(e) => setUntil(e.target.value)}
            />
          </div>
        )}

        {next && (
          <div
            className={`border px-3 py-2.5 text-[12px] leading-relaxed ${
              !nextValid
                ? 'border-red-600/30 bg-red-50 text-red-700'
                : expired
                  ? 'border-amber-500/40 bg-amber-50 text-amber-800'
                  : 'border-line bg-paper text-ink/70'
            }`}
          >
            {!nextValid ? (
              'Новая дата должна быть раньше текущего окончания.'
            ) : expired ? (
              <>
                Станет: до <strong>{formatDate(next.toISOString())}</strong> — срок уже истёк.{' '}
                {blockAt && blockAt.getTime() > now
                  ? `Касса проработает до ${formatDate(blockAt.toISOString())}, потом доступ закроется.`
                  : 'Доступ закроется сразу.'}
              </>
            ) : (
              <>
                Станет: до <strong className="text-ink">{formatDate(next.toISOString())}</strong> · осталось{' '}
                {nextDaysLeft} {pluralDays(nextDaysLeft ?? 0)}.
              </>
            )}
          </div>
        )}

        {sub.autoRenew && sub.status === 'active' && (
          <div className="flex items-start gap-2 border border-amber-500/40 bg-amber-50 px-3 py-2.5 text-[12px] leading-relaxed text-amber-800">
            <AlertTriangle size={14} strokeWidth={1.75} className="mt-0.5 shrink-0" />
            <span>
              К подписке привязана карта ЮKassa: в новую дату окончания автоматически спишется
              оплата за следующий период.
            </span>
          </div>
        )}

        {err && <p className="text-[12px] text-red-600">{err}</p>}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button className="btn-ghost" type="button" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button
            className="btn-dark"
            type="button"
            onClick={submit}
            disabled={busy || !nextValid}
          >
            <Minus size={14} strokeWidth={1.75} />
            {busy ? 'Сохраняю…' : 'Уменьшить срок'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Plan modal ─────────────────────────────────────────────────
// Тариф меняется только у этой точки; сумма — за одно заведение. Поля «доп.
// заведения» больше нет: у каждой точки владельца своя подписка.

function PlanModal({
  tenantId,
  currentPlanCode,
  currentBillingPeriod,
  onClose,
  onSaved,
}: {
  tenantId: string;
  currentPlanCode: string | null;
  currentBillingPeriod: 'MONTHLY' | 'YEARLY';
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [plans, setPlans] = useState<PlanItem[]>([]);
  const [planCode, setPlanCode] = useState<string>(currentPlanCode ?? '');
  const [billingPeriod, setBillingPeriod] = useState<'MONTHLY' | 'YEARLY'>(currentBillingPeriod);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    listPlans().then((r) => {
      setPlans(r.plans);
      if (!planCode && r.plans.length > 0) setPlanCode(r.plans[0].code);
    }).catch((e: Error) => setErr(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = useMemo(
    () => plans.find((p) => p.code === planCode) ?? null,
    [plans, planCode],
  );

  const price = selected
    ? billingPeriod === 'YEARLY'
      ? selected.priceYearKopecks
      : selected.priceMonthKopecks
    : 0;

  const submit = async () => {
    if (!planCode) return;
    setBusy(true);
    setErr(null);
    try {
      const body: ChangeTenantPlanBody = { planCode, billingPeriod };
      await apiChangeTenantPlan(tenantId, body);
      await onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Ошибка смены тарифа');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Смена тарифа" wide>
      <div className="space-y-5">
        <p className="text-[12px] text-ink/60">
          Оператор меняет тариф без списания — только у этого заведения, сумма за одну точку.
          Реальная оплата придёт с очередного списания подписки.
        </p>

        {plans.length === 0 ? (
          <p className="text-[13px] text-ink/50">Загрузка каталога…</p>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {plans.map((p) => {
              const active = p.code === planCode;
              const base = billingPeriod === 'YEARLY' ? p.priceYearKopecks : p.priceMonthKopecks;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPlanCode(p.code)}
                  className={`text-left border p-4 transition-colors ${
                    active ? 'border-ink bg-paper' : 'border-line hover:border-ink/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-[15px] font-medium">{p.name}</div>
                    {p.isPopular && <span className="badge-orange">Популярный</span>}
                  </div>
                  <div className="mt-2 font-mono text-[18px]">{formatKop(base)}</div>
                  <div className="text-[11px] text-ink/50">
                    / {billingPeriod === 'YEARLY' ? 'год' : 'мес'}
                  </div>
                  <div className="mt-3 space-y-1 text-[12px] text-ink/60">
                    <div>
                      Заведений:{' '}
                      <strong className="text-ink/80">
                        {p.maxEstablishments === null ? '∞' : p.maxEstablishments}
                      </strong>
                    </div>
                    <div>
                      Касс:{' '}
                      <strong className="text-ink/80">
                        {p.maxCashiers === null ? '∞' : p.maxCashiers}
                      </strong>
                    </div>
                    <div>
                      Пользователей:{' '}
                      <strong className="text-ink/80">
                        {p.maxUsers === null ? '∞' : p.maxUsers}
                      </strong>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div>
          <label className="eyebrow">Период оплаты</label>
          <div className="mt-2 inline-flex border border-line">
            <button
              type="button"
              className={`px-3 py-1.5 text-[12px] ${
                billingPeriod === 'MONTHLY' ? 'bg-ink text-white' : 'text-ink/70'
              }`}
              onClick={() => setBillingPeriod('MONTHLY')}
            >
              Месяц
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 text-[12px] ${
                billingPeriod === 'YEARLY' ? 'bg-ink text-white' : 'text-ink/70'
              }`}
              onClick={() => setBillingPeriod('YEARLY')}
            >
              Год
            </button>
          </div>
        </div>

        <div className="flex items-baseline justify-between border-t border-line pt-3">
          <span className="text-[12px] text-ink/60">
            Итого за {billingPeriod === 'YEARLY' ? 'год' : 'месяц'}
          </span>
          <span className="font-mono text-[22px] font-medium">{formatKop(price)}</span>
        </div>

        {err && <p className="text-[12px] text-red-600">{err}</p>}

        <div className="flex items-center justify-end gap-2">
          <button className="btn-ghost" type="button" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button
            className="btn-primary"
            type="button"
            onClick={submit}
            disabled={busy || !planCode}
          >
            Применить тариф
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Ручная активация оплаченного периода ───────────────────────
// Два режима одной формы, отличаются только деньгами:
//   cash   — клиент занёс наличные: в историю пишется платёж method='cash';
//   manual — доступ выдан без оплаты (партнёр, компенсация, тест): платежа нет,
//            чтобы не портить выручку. Срок в обоих случаях обязателен —
//            без него статус ACTIVE откатится часовым runLapseSweep.

function CashPaymentModal({
  tenantId,
  sub,
  variant = 'cash',
  onClose,
  onSaved,
}: {
  tenantId: string;
  sub: TenantDetailDto['subscription'];
  variant?: 'cash' | 'manual';
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const isManual = variant === 'manual';
  type Mode = 'MONTHLY' | 'YEARLY' | 'days' | 'until';
  const [mode, setMode] = useState<Mode>('MONTHLY');
  const [days, setDays] = useState<number>(30);
  const [until, setUntil] = useState<string>(
    new Date(Date.now() + 30 * 86400_000).toISOString().slice(0, 10),
  );
  const [amountRub, setAmountRub] = useState<string>(String(kopToRub(sub?.amount ?? 250000)));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const currentEnd = sub?.currentPeriodEnd ?? null;

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const body: ActivatePaidBody = {};
      if (mode === 'MONTHLY' || mode === 'YEARLY') {
        body.period = mode;
      } else if (mode === 'days') {
        if (!Number.isInteger(days) || days <= 0 || days > 1100) {
          throw new Error('Введите число дней от 1 до 1100');
        }
        body.days = days;
      } else {
        if (!until) throw new Error('Выберите дату');
        body.until = new Date(until + 'T23:59:59Z').toISOString();
      }
      if (isManual) {
        body.recordPayment = false;
      } else {
        const rub = Number(amountRub);
        if (!Number.isFinite(rub) || rub < 0) throw new Error('Некорректная сумма');
        body.amountKopecks = Math.round(rub * 100);
      }
      if (note.trim()) body.note = note.trim();
      await apiActivatePaid(tenantId, body);
      await onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Ошибка активации');
    } finally {
      setBusy(false);
    }
  };

  const modes: { key: Mode; label: string }[] = [
    { key: 'MONTHLY', label: 'Месяц' },
    { key: 'YEARLY', label: 'Год' },
    { key: 'days', label: 'N дней' },
    { key: 'until', label: 'До даты' },
  ];

  return (
    <Modal onClose={onClose} title={isManual ? 'Перевести в Active' : 'Оплата наличными'}>
      <div className="space-y-4">
        <div className="border border-line bg-paper px-3 py-2.5 text-[12px] leading-relaxed text-ink/70">
          Подписка станет <strong className="text-ink">ACTIVE</strong>, срок продлится от{' '}
          <strong className="text-ink">
            {currentEnd ? formatDate(currentEnd) : 'сегодняшней даты'}
          </strong>
          {isManual ? (
            <>
              {' '}— <strong className="text-ink">без платежа в истории</strong>: выручка не
              изменится. Для партнёрского доступа, компенсации и тестовых точек. Если клиент
              заплатил, используйте «Оплата наличными».
            </>
          ) : (
            <>
              {' '}и в историю добавится платёж <span className="font-mono">cash</span>. Используйте,
              когда клиент заплатил наличными (вне ЮKassa).
            </>
          )}
        </div>

        <div>
          <label className="eyebrow">Продлить на</label>
          <div className="mt-2 inline-flex flex-wrap border border-line">
            {modes.map((m) => (
              <button
                key={m.key}
                type="button"
                className={`px-3 py-1.5 text-[12px] ${
                  mode === m.key ? 'bg-ink text-white' : 'text-ink/70'
                }`}
                onClick={() => setMode(m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {mode === 'days' && (
          <div>
            <label className="eyebrow">Количество дней</label>
            <input
              className="input mt-2"
              type="number"
              min={1}
              max={1100}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            />
            <div className="mt-2 flex flex-wrap gap-1">
              {[30, 60, 90, 180, 365].map((n) => (
                <button
                  key={n}
                  type="button"
                  className="border border-line px-2 py-1 text-[11px] text-ink/70 hover:border-ink hover:text-ink"
                  onClick={() => setDays(n)}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}

        {mode === 'until' && (
          <div>
            <label className="eyebrow">Оплачено до</label>
            <input
              className="input mt-2 font-mono"
              type="date"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
            />
          </div>
        )}

        {!isManual && (
        <div>
          <label className="eyebrow">Принято, ₽</label>
          <input
            className="input mt-2"
            type="number"
            min={0}
            value={amountRub}
            onChange={(e) => setAmountRub(e.target.value)}
          />
          <p className="mt-1 text-[11px] text-ink/50">
            По умолчанию — сумма подписки вместе с подключёнными модулями. Впишите фактически
            принятую наличными.
          </p>
        </div>
        )}

        <div>
          <label className="eyebrow">Заметка (необязательно)</label>
          <input
            className="input mt-2"
            placeholder={isManual ? 'партнёр, компенсация…' : 'нал, чек №…'}
            maxLength={300}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        {err && <p className="text-[12px] text-red-600">{err}</p>}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button className="btn-ghost" type="button" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button className="btn-primary" type="button" onClick={submit} disabled={busy}>
            {isManual ? <Check size={14} strokeWidth={1.75} /> : <Banknote size={14} strokeWidth={1.75} />}
            {busy ? 'Активирую…' : isManual ? 'Перевести в Active' : 'Активировать оплату'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Addon picker modal ─────────────────────────────────────────

function AddonPickerModal({
  tenantId,
  existingCodes,
  onClose,
  onSaved,
}: {
  tenantId: string;
  existingCodes: string[];
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [catalog, setCatalog] = useState<AddonCatalogItem[]>([]);
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  useEffect(() => {
    getAddonCatalog().then((r) => setCatalog(r.items)).catch((e: Error) => setErr(e.message));
  }, []);

  const attach = async (item: AddonCatalogItem) => {
    const quantity = item.maxQuantity ? quantities[item.code] ?? 1 : undefined;
    if (quantity !== undefined && (!Number.isInteger(quantity) || quantity < 1 || quantity > (item.maxQuantity ?? 1))) {
      setErr(`Количество — от 1 до ${item.maxQuantity}`);
      return;
    }
    setBusyCode(item.code);
    setErr(null);
    try {
      await attachTenantAddon(tenantId, item.code, quantity);
      await onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Ошибка подключения');
    } finally {
      setBusyCode(null);
    }
  };

  return (
    <Modal onClose={onClose} title="Подключить модуль" wide>
      {err && <p className="mb-3 text-[12px] text-red-600">{err}</p>}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {catalog.map((a) => {
          const already = existingCodes.includes(a.code);
          return (
            <div key={a.code} className="border border-line p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[14px] font-medium">{a.name}</div>
                  <div className="mt-1 font-mono text-[11px] text-ink/50">{a.code}</div>
                </div>
                <div className="text-right font-mono text-[14px] font-medium">
                  {formatKop(a.priceMonthKopecks)}
                  <div className="text-[10px] font-normal text-ink/40">/ мес</div>
                </div>
              </div>
              <p className="mt-2 text-[12px] leading-relaxed text-ink/60">{a.description}</p>
              <div className="mt-3 flex items-center justify-end gap-2">
                {already ? (
                  <span className="text-[12px] text-ink/40">
                    {a.maxQuantity ? 'Уже подключено — количество меняется в списке модулей' : 'Уже подключено'}
                  </span>
                ) : (
                  <>
                    {a.maxQuantity && (
                      <label className="flex items-center gap-2 text-[12px] text-ink/60">
                        Кол-во
                        <input
                          className="input w-16 py-1.5 text-center"
                          type="number"
                          min={1}
                          max={a.maxQuantity}
                          value={quantities[a.code] ?? 1}
                          onChange={(e) =>
                            setQuantities((cur) => ({ ...cur, [a.code]: Number(e.target.value) }))
                          }
                        />
                      </label>
                    )}
                    <button
                      className="btn-outline"
                      type="button"
                      onClick={() => attach(a)}
                      disabled={busyCode === a.code}
                    >
                      <Plus size={13} strokeWidth={1.75} />
                      Подключить
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
        {catalog.length === 0 && !err && (
          <p className="col-span-full py-6 text-center text-[13px] text-ink/50">Загрузка каталога…</p>
        )}
      </div>
    </Modal>
  );
}

// ─── Generic UI bits ────────────────────────────────────────────

function Modal({
  title,
  children,
  onClose,
  wide,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-ink/40 p-0 sm:items-center sm:px-4 sm:py-10"
      onClick={onClose}
    >
      <div
        className={`card relative w-full ${wide ? 'max-w-3xl' : 'max-w-md'} p-5 shadow-lg sm:p-6 max-h-[92vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="absolute right-3 top-3 p-1 text-ink/50 hover:text-ink"
          onClick={onClose}
          aria-label="Закрыть"
        >
          <X size={16} />
        </button>
        <h3 className="mb-4 text-[16px] font-medium tracking-tight">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line pb-2 last:border-b-0 last:pb-0">
      <dt className="text-ink/50">{label}</dt>
      <dd className="text-right text-ink">{value}</dd>
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[11px] font-medium uppercase tracking-[0.1em] text-ink/50">
        {label}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function pluralEstablishments(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'заведение';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'заведения';
  return 'заведений';
}

function paymentBadgeClass(status: string): string {
  switch (status) {
    case 'SUCCEEDED': return 'badge-green';
    case 'FAILED':    return 'badge-red';
    case 'REFUNDED':  return 'badge-ink';
    default:          return 'badge-amber';
  }
}

function paymentStatusLabel(status: string): string {
  switch (status) {
    case 'SUCCEEDED': return 'успешно';
    case 'FAILED':    return 'ошибка';
    case 'REFUNDED':  return 'возврат';
    case 'PENDING':   return 'в ожидании';
    default:          return status.toLowerCase();
  }
}
